import {
  FluidElementType,
  FluidMenuItem,
  FluidMenuState,
  isNullOrUndefined,
} from '@lmig/fluid-core';

// ================================================================ //
// -- Tree Menu State Management Utilities (Public)

/**
 * Initializes derived state that includes a deep copy of the "menuItems" Prop,
 * the current selected menu item, the "consecutive path" to the selected item,
 * and a flattened array of all menu items that contain a submenu.
 * @param host
 * @return - FluidMenuState object, or null if "menuItems" @Prop is undefined
 */
export const initializeTreeMenuState = (host): FluidMenuState | null => {
  if (!host.menuItems?.length) return;

  const items = cloneMenuItems(host.menuItems);

  return {
    menuItems: items,
    submenuItems: initializeSubmenus(items, host.collapsibleSubmenus),
    selectedItem: getSelectedItem(items),
    selectedItemPath: getSelectedItemPath(items),
  };
};

/**
 * Returns an array of menu item(s) that constitute(s) a "consecutive path"
 * to the currently selected menu item. For example, if the selected menu
 * item is nested within a submenu, "selectedItemPath" will consist of the
 * selected item's parent menu item (index 0) & the selected item (index 1).
 * Can be used to populate FLUID Breadcrumb's "trail" Prop.
 * @param items - array of FluidMenuItems to iterate through
 * @return - array containing the selected item's "path"
 */
export const getSelectedItemPath = (
  items: FluidMenuItem[]
): FluidMenuItem[] => {
  return (
    items.reduce((path: FluidMenuItem[], item) => {
      switch (true) {
        case !!path?.length:
          return path;
        case item.selected:
          return [item];
        case hasSubmenu(item): {
          const submenuItemPath = getSelectedItemPath(item.submenu);
          return submenuItemPath?.length
            ? [item, ...submenuItemPath]
            : submenuItemPath;
        }
      }
    }, []) ?? []
  );
};

/**
 * Returns true if menu item contains array of "submenu" menu items.
 * @param menuItem - the menu item in question
 */
export const hasSubmenu = (menuItem: FluidMenuItem): boolean => {
  return !!menuItem?.submenu?.length;
};

/**
 * Prevents the default behavior of anchor-based menu items on click.
 * Used when app's router is used for navigation (i.e. FluidMenuItem
 * "route" property is used instead of "href").
 * @param menuItem - the menu item that was clicked
 * @param event - the click event
 */
export const preventLinkDefault = (
  menuItem: FluidMenuItem,
  event: MouseEvent
): void => {
  if (
    menuItem.disabled ||
    (!menuItem.href && menuItem.elementType === FluidElementType.ANCHOR)
  ) {
    event.preventDefault();
  }
};

/**
 * Recursively iterates through all menu items & submenus & returns a
 * deep copy of original FluidMenuItem array.
 * @param items - array of FluidMenuItem objects to be cloned
 */
export const cloneMenuItems = (items: FluidMenuItem[]): FluidMenuItem[] => {
  return items.map((item) => {
    return {
      ...item,
      ...(hasSubmenu(item) && { submenu: cloneMenuItems(item.submenu) }),
    };
  });
};

/**
 * Recursive function that finds & returns the menu item with provided
 * "id" property.
 * @param items - the current array to iterate through
 * @param targetId - the "id" property to find
 */
export const getMenuItemById = (
  items: FluidMenuItem[],
  targetId: string
): FluidMenuItem => {
  if (isNullOrUndefined(items) || !targetId) return;

  return items.reduce((targetItem: FluidMenuItem, item) => {
    switch (true) {
      case !!targetItem:
        return targetItem;
      case item.id === targetId:
        return item;
      case hasSubmenu(item):
        return getMenuItemById(item.submenu, targetId);
    }
  }, null);
};

// ================================================================ //
// -- Tree Menu State Management Utilities (Private)

/**
 * Recursive function that initializes "submenuItems" property of
 * "_treeMenuState". Returns a flattened array of all menuItems that
 * contain a submenu, regardless of nested location within "menuItems" array.
 * Also sets each undefined "submenuCollapsible" property to the value of
 * Tree Menu's "collapsibleSubmenus" @Prop. If submenu is NOT collapsible,
 * ensures that submenu is rendered open by default.
 * @param items - the current array to iterate through
 * @param collapsible - the value of "collapsibleSubmenus" @Prop
 * @param depth - the current nested submenu depth
 * @return - a flattened array of all menuItems containing a submenu
 */
const initializeSubmenus = (
  items: FluidMenuItem[],
  collapsible: boolean,
  depth = 1
): FluidMenuItem[] => {
  return items.reduce((submenus: FluidMenuItem[], item) => {
    if (!hasSubmenu(item)) return submenus;
    item._submenuDepth = depth;
    item.submenuCollapsible = item.submenuCollapsible ?? collapsible;
    if (!item.submenuCollapsible) item.submenuOpen = true;
    return [
      ...submenus,
      item,
      ...initializeSubmenus(item.submenu, collapsible, depth + 1),
    ];
  }, []);
};

/**
 * Recursively searches the "menuItems" array (within "_treeMenuState") for selected
 * menuItem (i.e. "selected" property is true). If selected item is nested within
 * closed submenus, this function also opens all submenus necessary to display
 * the selected item. Run on first render & when "menuItems" Prop is updated.
 * @param items - the current array to iterate through
 * @return - the first selected menu item, or null if no selected item found
 */
const getSelectedItem = (items: FluidMenuItem[]): FluidMenuItem => {
  return items.reduce((selectedItem: FluidMenuItem, item) => {
    if (selectedItem) return selectedItem;
    if (item.selected) return item;
    if (hasSubmenu(item)) {
      const submenuItem = getSelectedItem(item.submenu);
      if (submenuItem) item.submenuOpen = true;
      return submenuItem;
    }
  }, null);
};
